import { useMutation } from "@apollo/react-hooks";
import { useState } from "react";
import _ from "lodash";
import { Button } from "@/ui";
import { Icon } from "@/components";
import useNotification from "../components/useNotification";
import { shouldRecalculateCoordinates } from "utils/utils";
import { MUTATION_UPDATE_STORE } from "apollo/module-operations/store";

const SaveIcon = () => <Icon icon="save" />;

const useGeoUpdate = (
    refetchQueries,
    options,
    isValid,
    isSubmited = null,
    oldAddress,
    refetchStore,
    updateInputs,
) => {
    const notify = useNotification();
    const [isProcessing, setIsProcessing] = useState(false);

    const mapGeocodeStatusToMsg = status => {
        switch (status) {
            case "ZERO_RESULTS":
                return "No coordinates were found for that address.";
            case "INVALID_REQUEST":
                return "The address is invalid or incomplete.";
            case "REQUEST_DENIED":
                return "The Maps API rejected the request (check your API key, domains, or billing).";
            case "OVER_QUERY_LIMIT":
                return "The query limit has been exceeded. Please try again later.";
            case "UNKNOWN_ERROR":
                return "Temporary Google Maps error. Please try again.";
            case "GOOGLE_NOT_LOADED":
                return "Temporary Google Maps error. Please try again.";
            default:
                return `Geocoding failed: ${status || "ERROR"}`;
        }
    };

    const geocodeAddress = address => {
        return new Promise((resolve, reject) => {
            if (!window.google || !window.google.maps) {
                reject(new Error("GOOGLE_NOT_LOADED"));
                return;
            }

            const geocoder = new window.google.maps.Geocoder();
            geocoder.geocode({ address }, (results, status) => {
                if (status === "OK" && results[0]) {
                    const location = results[0].geometry.location;
                    resolve({ lat: location.lat(), lng: location.lng() });
                } else {
                    reject(new Error(status));
                }
            });
        });
    };

    const compactAddressString = input => {
        // Build the string and remove extra spaces/commas to validate
        const full = `${input.address1 || ""} ${input.address2 || ""}, ${
            input.city || ""
        }, ${input.province_code || ""} ${input.zip || ""}, ${
            input.country_code || ""
        }`.trim();
        return { full, bare: full.replace(/[,\s]/g, "") };
    };

    const getCoordinatesFromAddress = async input => {
        const { full, bare } = compactAddressString(input);

        // Avoid useless calls when the address is empty/incomplete
        if (!bare) {
            notify("The address is empty or incomplete.", "warning");
            return input;
        }

        try {
            const { lat, lng } = await geocodeAddress(full);
            input.latitude = lat;
            input.longitude = lng;
        } catch (error) {
            const msg = mapGeocodeStatusToMsg(error && error.message);
            input.latitude = null;
            input.longitude = null;
            notify(msg, "warning");
        }

        return input;
    };

    const [mutate] = useMutation(MUTATION_UPDATE_STORE, {
        onCompleted: () => {
            refetchStore().then(({ data }) => {
                if (!data?.getStore) return;

                const inputs = _.cloneDeep(data.getStore);
                updateInputs(inputs);
            });
            setIsProcessing(false);
            notify("Store Updated Successfully.", "success");
        },
        onError: () => {
            notify("Error Updating The Store.", "error");
            setIsProcessing(false);
        },
    });

    const generateGeoAndUpdate = async () => {
        if (isSubmited) isSubmited(true);
        if (!isValid()) return;

        setIsProcessing(true);
        let input = options.variables.input;

        if (
            (oldAddress && shouldRecalculateCoordinates(oldAddress, input)) ||
            (!input.latitude && !input.longitude)
        ) {
            input = await getCoordinatesFromAddress(input);
        }

        const newOptions = _.omit(
            {
                ...options,
                variables: {
                    ...options.variables,
                    input,
                },
            },
            ["variables.input.ignore_whitespace"],
        );

        mutate(newOptions);
    };

    const generateGeoAndUpdateStoreButton = (
        <Button
            disabled={isProcessing}
            iconLeft={<SaveIcon />}
            key="general-form"
            loading={isProcessing}
            onClick={generateGeoAndUpdate}
            variant="primary"
        >
            Update
        </Button>
    );

    return { generateGeoAndUpdateStoreButton };
};

export default useGeoUpdate;
